import { ExtDispatch } from '@moderepo/mode-data-state-robot-cloud';
import { deleteDeviceKVStore, updateDeviceKVStore, UserAppDataStateAction } from '@moderepo/mode-data-state-user-app';
import { ApiError, DeviceConfigParamSet, DeviceConfigParamType, DeviceConfigParamValueType, KeyValuePair } from '@moderepo/mode-internal-apis';
import { DialogResponse, DialogResponseCode, DialogType, showDialog, showErrorNotification, UIAction } from '@moderepo/mode-ui-state';
import { TFunction } from 'i18next';
import { DeviceConfigInputErrors, DeviceConfigInputs, getInitialDeviceConfigInputs, normalizeDeviceConfigInputs }
    from '../../components/devices/utils';
import { genericValidateArrayInput, genericValidateBooleanInput, genericValidateNumberInput, genericValidateObjectInput, genericValidateStringInput,
    sortObjectAttributes } from '../../utils/utils';



export const validateDeviceConfigInputs = (
    deviceConfigInputs: DeviceConfigInputs, deviceConfigSchema: readonly DeviceConfigParamSet[] | undefined, isNewInputs: boolean, trans: TFunction,
): DeviceConfigInputErrors => {

    if (!deviceConfigSchema || !deviceConfigInputs) {
        return [];
    }

    return deviceConfigInputs.map((paramSetInputs, paramSetIndex) => {

        // Repeatable device config schemas will generate multiple DDPs and their keys are dynamically created based on the schema template
        // and one or more of the input values. It is possible that the input values used for generating the DDP have the same value therefore
        // causing DDP with the same key to be created. So we need to validate the DDP keys as well to make sure we don't have duplicate
        const ddpKeys = {};

        return paramSetInputs.map((paramSetInstanceInputs) => {

            const paramSetSchema = deviceConfigSchema[paramSetIndex];
            const inputErrors = {};

            // For each param in the paramSet, check if the inputs object contain value for the param and if they are valid
            paramSetSchema.parameters.forEach((paramSettings) => {
                const paramKey = paramSettings.key;
                const inputValue = paramSetInstanceInputs[paramKey];

                if (paramSettings.type === DeviceConfigParamType.NUMBER) {
                    inputErrors[paramKey] = genericValidateNumberInput(inputValue, paramSettings, isNewInputs, trans);
                } else if (paramSettings.type === DeviceConfigParamType.STRING) {
                    inputErrors[paramKey] = genericValidateStringInput(inputValue, paramSettings, isNewInputs, trans);
                } else if (paramSettings.type === DeviceConfigParamType.BOOLEAN) {
                    inputErrors[paramKey] = genericValidateBooleanInput(inputValue, paramSettings, trans);
                } else if (paramSettings.type === DeviceConfigParamType.ARRAY) {
                    inputErrors[paramKey] = genericValidateArrayInput(inputValue, paramSettings, trans);
                } else if (paramSettings.type === DeviceConfigParamType.OBJECT) {
                    inputErrors[paramKey] = genericValidateObjectInput(inputValue, paramSettings, trans);
                }
            });

            if (paramSetSchema.repeatable) {
                // Get the ddpKey that would be generated based on the schema and the inputs
                const newDDPKey = createDeviceConfigDDPKey(paramSetSchema.keyName, paramSetInstanceInputs);

                // If the ddpKey is already created for one of the previous paramSetInputs then we have an error
                if (ddpKeys[newDDPKey]) {
                    // Set the error message using the schema's name as key since we don't really know which field is
                    // causing the error. The DDP key template might include values from multiple input fields.
                    inputErrors[paramSetSchema.name] = trans('error.input.device_config.duplicate_ddp_key', { ddp_key: newDDPKey });
                }

                // Add the newDDPKey to the map so we can check it later.
                ddpKeys[newDDPKey] = newDDPKey;
            }

            return inputErrors;
        });
    });
};



/**
 * Give a DDP keyName template that contains placeholders and an object containing ddpValue, return a ddpKey which has the placeholders
 * replaced with values from ddpValue. E.g.
 *
 * Given
 *      keyNameTemplate="device::{{deviceId}}::module::{{moduleId}}:configuration"
 *      ddpValueObject={
 *          deviceId: 123,
 *          ip: "127.0.0.1",
 *          port: 80,
 *          moduleId: "abcdef"
 *      }
 *
 * Return
 *      device::123::module::abcdef:configuration
 */
export const createDeviceConfigDDPKey = (keyNameTemplate: string, ddpValueObject: {
    [paramKey: string]: DeviceConfigParamValueType | undefined;
}): string => {
    if (keyNameTemplate && ddpValueObject && typeof ddpValueObject === 'object') {

        // For each key/value in the ddpValueObject, find the {{key}} in the resultDDPKey and replace it with the value.
        // e.g. if key="moduleId" and value="abcdef" and resultDDPKey="my_ddp_key::{{moduleId}}::configuration". This will return
        // "my_ddp_key::abcdef::configuration"
        return Object.entries(ddpValueObject).reduce((resultDDPKey: string, [paramKey, paramValue]): string => {

            // Only replace placeholder if paramValue is a number or string. We won't handle other param value types e.g. boolean, array, object
            if (typeof paramValue === 'number' || typeof paramValue === 'string') {
                // We will call replaceAll incase there are more than 1 {{paramKey}} in the template
                // Also, we will allow spaces between {{}} e.g. {{paramKey}}, {{ paramKey }}, {{     paramKey}}
                return resultDDPKey.replaceAll(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), paramValue.toString());
            }

            return resultDDPKey;
        }, keyNameTemplate);    // Start with the keyNameTemplate
    }
    return keyNameTemplate;
};


/**
 * This function checks if a given DDP was created based on the specified paramSet.
 */
export const isDDPInstanceOfParamSet = (ddp: KeyValuePair, paramSet: DeviceConfigParamSet): boolean => {
    // If DDP value is not an object or it if is an object but it is an Array then we know the ddp is NOT the config for the paramSet
    if (typeof ddp.value !== 'object' || ddp.value instanceof Array) {
        return false;
    }

    // Try to re-produce a ddpKey based on the paramSet and ddp.value as we did when we save the device config.
    // This was how we generated the DDP key. So if the DDP we re-produce matches the ddp.key then we can assume the DDP was created
    // from the paramSet
    const generatedDDPKey = createDeviceConfigDDPKey(paramSet.keyName, ddp.value);
    return generatedDDPKey === ddp.key;
};



/**
 * Given a device config schema and an array of DDP, create device config objects based on the data from DDPs and schema.
 * For each param set in the schema, we will try to find the DDP that is being used for storing values for the schema. If we find one,
 * we will create an object that has all the values for the param set using the DDP's values. If we can't find a DDP that matches the
 * param set then return [] for it.
 *
 * If the schema has 2 sets of params, this will return an array of object like this.
 *    [
 *        // Data for the 1st set of params. The reason this is an Array of Objects is because a param set can be "repeatable" therefore there
 *        // can be multiple instances of these objects
 *        [
 *            {
 *                "enabledOTAUBootCheck": true,
 *                "objectParam": {
 *                    "x": "34916",
 *                    "y": "abcdef"
 *                },
 *                "sshPrivateKeyFilePath": "/etc/path",
 *                "tsdbBulkUploadLabel": "label"
 *            }
 *        ],
 *        // Data for 2nd set of params which is repeatable and has 2 instances
 *        [
 *            {
 *                "interval": 15,
 *                "peripheralDriverName": "xxx"
 *            },
 *            {
 *                "interval": 30,
 *                "peripheralDriverName": "yyy"
 *            }
 *        ]
 *    ]
 */
export const createDeviceConfigInputs = (
    deviceId: number, deviceConfigSchema: readonly DeviceConfigParamSet[], ddps: readonly KeyValuePair[],
): DeviceConfigInputs => {

    // Go through the schema's param sets and find the DDP associated with each param set and combine them to create a rawDeviceConfigObject.
    // This object contains the raw DDP values which mean the Arrays and Objects values are still in Array and Object format
    const rawDeviceConfigObject = deviceConfigSchema.map((paramSet) => {
        if (paramSet.repeatable) {
            // There can be 0 or more DDP that are created based on this paramSet schema. We need to find ALL DDP that has key name
            // matches the paramSet's keyName.
            return ddps
                // filter out DDP that is NOT instances of the paramSet
                .filter((ddp) => {
                    return isDDPInstanceOfParamSet(ddp, paramSet);
                })
                // For each DDP that we found that matches the paramSet, return its values
                .map((kvPair) => {
                    return kvPair.value;
                });
        }

        // There should be only at most 1 DDP that are created based on this paramSet. We need to find 1 DDP that has keyName
        // matches the paramSet's keyName. And even though there can only be 1 DDP instance, we will still return an ARRAY of
        // instances.
        const ddp = ddps.find((ddp) => {
            return isDDPInstanceOfParamSet(ddp, paramSet);
        });
        if (ddp) {
            return [ddp.value];
        }
        return [];
    });


    // Transform the rawDeviceConfigObject into an object that can be used in the Device Config input fields. This will convert all
    // Array and Object values to strings.
    return getInitialDeviceConfigInputs(
        deviceId, deviceConfigSchema, rawDeviceConfigObject,
    );
};



/**
 * Clear the input error for 1 specific param in the device config input errors object. A device config has an Array of inputs, one for each
 * set of params. And in each set of params is an Array of inputs for each instance. And each item in those array are Objects that has a mapping
 * of the error for each field. Here is the structure of the input error objects.
 *
 *      [
 *          [
 *              {
 *                  fileName: "invalid name",
 *                  fileSize: "file size out of range"
 *              }
 *          ],
 *          [
 *              {
 *                  instanceName: "name is not provided",
 *                  interval: "interval is not selected"
 *              },
 *              {
 *                  instanceName: undefined,
 *                  interval: undefined
 *              }
 *          ]
 *      ]
 *
 * So if we want to clear the error message for the first "instanceName", we would pass these values to the function
 * paramName="instanceName", paramSetIndex=1, paramSetInstanceIndex=0
 */
export const clearDeviceConfigInputErrorForParamName = (
    currentInputErrors: DeviceConfigInputErrors,
    paramName: string,
    paramSetIndex: number,
    paramSetInstanceIndex: number,
) => {
    // Check to see if there is some input error message for the given paramKey in the currentInputErrors object.
    // If it is undefined then it means the input does not have error previously so we don't need to clear it.
    // Return the old state so that we don't rerender the UI
    if (currentInputErrors?.[paramSetIndex]?.[paramSetInstanceIndex]?.[paramName] === undefined) {
        return currentInputErrors;
    }

    // There was some input error for the given paramKey. Set it to undefined to clear it and return the new inputErrors state
    const newInputErrors = [...currentInputErrors];
    if (!newInputErrors[paramSetIndex]) {
        newInputErrors[paramSetIndex] = [];
    }
    if (!newInputErrors[paramSetIndex][paramSetInstanceIndex]) {
        newInputErrors[paramSetIndex][paramSetInstanceIndex] = {};
    }

    delete newInputErrors[paramSetIndex][paramSetInstanceIndex][paramName];
    return newInputErrors;
};



/**
 * Give a "newDeviceConfigInputs" and a schema for those inputs, create the update DDP actions needed to write those inputs to the DDP.
 * And if a "previousDeviceConfigInputs", compare the new and previous inputs to detect device config param set instances that are deleted.
 * If we find any inputs for param set instances deleted, we will need to delete the DDP for those param set instances as well.
 * This function DOES NOT make API all, all it does it create actions to update the DDP and add the actions to the provided
 * "updateDeviceDDPActions" array. And for each DDP that need to be deleted, this function will add the DDP key to the provided
 * "ddpKeysToBeDeleted" array.
 */
export const createUpdateDeviceConfigDDPActions = (
    deviceId: number,
    deviceConfigSchema: readonly DeviceConfigParamSet[],
    newDeviceConfigInputs: DeviceConfigInputs,
    previousDeviceConfigInputs: DeviceConfigInputs | undefined,
    allDDPs: readonly KeyValuePair[] | undefined,
    updateDeviceDDPActions: Promise<void>[],
    ddpKeysToBeDeleted: string[],
    appDataDispatch: ExtDispatch<UserAppDataStateAction>,
) => {

    // Normalize the new inputs and previous inputs (if there is one). This will make sure the values in the object are converted to the
    // right type.
    const normalizedDeviceConfigInputs = normalizeDeviceConfigInputs(
        newDeviceConfigInputs, deviceConfigSchema, false,
    );

    // The config inputs is an object the contains the inputs for the entire schema. Each schema contains an array of param sets and the
    // inputs for each param set will be store in ONE DDP. Therefore this will loop through the config for each param set and create
    // action to create/update 1 DDP.
    // eslint-disable-next-line no-loop-func
    normalizedDeviceConfigInputs.forEach((paramSetInputs, paramSetIndex) => {
        // Create 1 action to write to DDP for each paramSet instance. This will work for both repeatable and not repeatable paramSets.
        // none-repeatable paramSets will just gonna have 1 instance
        const paramSetSchema = deviceConfigSchema[paramSetIndex];

        // If this paramSetSchema is a "repeatable" param set then create an array of DDP keys and compile a list of DDP keys to be
        // generated for the config instances.
        const newRepeatableDDPKeys: string[] = [];

        paramSetInputs.forEach((paramSetInstanceInputs) => {
            // call helper function to create ddpKey based on the keyName template and paramSetInstanceInputs
            const ddpKey = createDeviceConfigDDPKey(paramSetSchema.keyName, paramSetInstanceInputs);

            // Find a DDP with the same ddpKey
            const existingDDP = allDDPs?.find((ddp) => {
                return ddp.key === ddpKey;
            });

            // Before we add a new action to update the DDP, compare the NEW DDP values with the previous value to see if it changed.
            // If the stringified version of these DDP values are different then we will assume the values are different and will make
            // API call to update the DDP. Or if the ddp does not exists yet, also make API call to create one.
            if (!existingDDP
                || JSON.stringify(sortObjectAttributes(existingDDP.value)) !== JSON.stringify(sortObjectAttributes(paramSetInstanceInputs))) {
                updateDeviceDDPActions.push(appDataDispatch(updateDeviceKVStore(deviceId, ddpKey, paramSetInstanceInputs)));
            }

            if (paramSetSchema.repeatable) {
                newRepeatableDDPKeys.push(ddpKey);
            }
        });

        const existingDeviceConfigs = previousDeviceConfigInputs?.[paramSetIndex];
        if (paramSetSchema.repeatable && existingDeviceConfigs && existingDeviceConfigs.length > 0) {
            // If there are existing deviceConfigs for this paramSetSchema then check if these configs are still needed.
            // To do this, we have to get the DDP for each of the `existingDeviceConfigs` and see if it still exist in the
            // "newRepeatableDDPKeys". If it is not in the "newRepeatableDDPKeys" then we can assume the user deleted the config therefore
            // we can delete the DDP
            existingDeviceConfigs.forEach((oneDeviceConfig) => {
                // Get the DDP key for the deviceConfig and see if it is in the "newRepeatableDDPKeys". If it is not then add it to
                // the "ddpKeysToBeDeleted"
                const ddpKey = createDeviceConfigDDPKey(paramSetSchema.keyName, oneDeviceConfig);
                if (!newRepeatableDDPKeys.includes(ddpKey)) {
                    ddpKeysToBeDeleted.push(ddpKey);
                }
            });
        }
    });
};



/**
 * Delete the provided array of DDP keys that are no longer used.
 */
export const deleteUnusedDDPs = async (
    deviceId: number,
    ddpKeysToBeDeleted: string[],
    trans: TFunction,
    uiDispatch: React.Dispatch<UIAction>,
    appDataDispatch: ExtDispatch<UserAppDataStateAction>,
) => {
    if (ddpKeysToBeDeleted.length > 0) {

        // Confirm before deleting the DDP
        uiDispatch(showDialog({
            type   : DialogType.CONFIRM,
            title  : 'dialog.confirm_remove_kv_pair.title',
            message: trans('dialog.confirm_delete_device_config_ddps.message', { ddp_keys: ddpKeysToBeDeleted.map((key) => {
                return `"${key}"`;
            }).join(', ') }),
            positiveButton: 'label.delete',
            negativeButton: 'label.no',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const actions: Promise<void>[] = [];
                    ddpKeysToBeDeleted.forEach((ddpKey) => {
                        actions.push(appDataDispatch(deleteDeviceKVStore(deviceId, ddpKey)));
                    });

                    // Dispatch all actions and wait for response
                    try {
                        await Promise.allSettled(actions);
                    } catch (error) {
                        uiDispatch(showErrorNotification((error as ApiError).code));
                    }
                }
            },
        }));

    }
};
